import React, { useEffect, useMemo, useState } from 'react';
import { useCampaign } from '../../contexts/CampaignContext';
import { useAuth } from '../../contexts/AuthContext';
import { getShop } from '../../lib/shops';
import ShopPanel from './ShopPanel';

// Proximity-gated entry point for the shop. When the local player's token is
// within 5 cells (Chebyshev) of any token with `shopId` set, render a button
// per merchant: "🛒 Shop with {token name}". Tap → opens ShopPanel.

const PROXIMITY_CELLS = 2;

export default function NearbyShopButton() {
  const { tokens } = useCampaign();
  const { auth } = useAuth();
  const playerId = auth?.playerId;
  const [openShop, setOpenShop] = useState(null); // { shopId, shopkeeperName }

  // My token (player-owned).
  const myToken = useMemo(() => {
    if (!playerId) return null;
    return Object.values(tokens || {}).find((t) => t.ownerId === playerId) || null;
  }, [tokens, playerId]);

  // Merchant tokens within range.
  const inRange = useMemo(() => {
    if (!myToken) return [];
    return Object.values(tokens || {}).filter((t) => {
      if (!t.shopId) return false;
      if (!getShop(t.shopId)) return false;
      const dx = Math.abs(t.x - myToken.x);
      const dy = Math.abs(t.y - myToken.y);
      return Math.max(dx, dy) <= PROXIMITY_CELLS;
    });
  }, [tokens, myToken]);

  // Auto-close the open shop when the player walks out of range of THAT
  // merchant. Mid-purchase taps land before the snapshot moves them away;
  // moving deliberately closes the modal so it doesn't stay stuck on screen.
  useEffect(() => {
    if (!openShop) return;
    const stillNear = inRange.some((t) => t.shopId === openShop.shopId);
    if (!stillNear) setOpenShop(null);
  }, [inRange, openShop]);

  if (inRange.length === 0 && !openShop) return null;

  return (
    <>
      {inRange.length > 0 && (
        <div style={styles.wrap}>
          {inRange.map((t) => {
            const shop = getShop(t.shopId);
            return (
              <button
                key={t.id}
                style={styles.btn}
                onClick={() => setOpenShop({ shopId: t.shopId, shopkeeperName: t.name })}
                title={`Open ${shop.name}`}
              >
                <span style={styles.icon}>🛒</span>
                <span style={styles.label}>Shop with {t.name}</span>
                <span style={styles.shopName}>{shop.icon} {shop.name}</span>
              </button>
            );
          })}
        </div>
      )}
      {openShop && (
        <ShopPanel
          shopId={openShop.shopId}
          shopkeeperName={openShop.shopkeeperName}
          onClose={() => setOpenShop(null)}
        />
      )}
    </>
  );
}

const styles = {
  wrap: {
    position: 'fixed', bottom: 220, left: '50%', transform: 'translateX(-50%)',
    zIndex: 40, display: 'flex', flexDirection: 'column', gap: 6, pointerEvents: 'none',
  },
  btn: {
    pointerEvents: 'auto',
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '10px 14px',
    background: 'linear-gradient(180deg, rgba(212,168,71,0.95), rgba(166,131,48,0.95))',
    border: '2px solid #d4a847',
    color: '#1a0e00',
    fontFamily: "'Press Start 2P', monospace",
    fontWeight: 800,
    fontSize: 13,
    cursor: 'pointer',
    borderRadius: 6,
    boxShadow: '0 6px 18px rgba(212,168,71,0.45)',
    letterSpacing: '0.04em',
  },
  icon:     { fontSize: 16 },
  label:    { },
  shopName: { fontSize: 11, color: '#3a2a10', opacity: 0.8, fontWeight: 700 },
};
